import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  background: ${({ isScrolled, theme }) => 
    isScrolled 
      ? 'rgba(255, 255, 255, 0.8)' 
      : 'rgba(255, 255, 255, 0.1)'};
  border-bottom: ${({ isScrolled, theme }) => 
    isScrolled 
      ? `1px solid ${theme.colors.border}` 
      : '1px solid transparent'};
  transition: ${props => props.theme.transition};
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.md};
  }
`;

const Logo = styled(motion.div)`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ isScrolled, theme }) => 
    isScrolled ? theme.colors.text : theme.colors.text};
  display: flex;
  align-items: center;
  cursor: pointer;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(motion.div)`
  font-weight: 400;
  font-size: 1rem;
  color: ${({ isScrolled, theme, $isActive }) => {
    if ($isActive) return theme.colors.primary;
    return isScrolled ? theme.colors.text : theme.colors.text;
  }};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.radius.small};
  transition: ${props => props.theme.transition};
  position: relative;
  
  &:hover {
    background: ${({ isScrolled, theme }) => 
      isScrolled 
        ? 'rgba(0, 122, 255, 0.08)' 
        : 'rgba(0, 122, 255, 0.08)'};
  }
`;

const ContactButton = styled(motion.button)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.radius.medium};
  font-weight: 500;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: ${props => props.theme.transition};
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const MobileMenuButton = styled(motion.button)`
  background: none;
  border: none;
  color: ${({ isScrolled, theme }) => 
    isScrolled ? theme.colors.text : theme.colors.text};
  font-size: 1.2rem;
  cursor: pointer;
  display: none;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.radius.small};
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 320px;
  height: 100vh;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  z-index: 200;
  border-left: 1px solid ${props => props.theme.colors.border};
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const MobileNavLink = styled(motion.div)`
  font-weight: 400;
  color: ${({ $isActive, theme }) => 
    $isActive ? theme.colors.primary : theme.colors.text};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.radius.small};
  font-size: 1.1rem;
  cursor: pointer;
  transition: ${props => props.theme.transition};
  
  &:hover {
    background: rgba(0, 122, 255, 0.05);
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  z-index: 150;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text};
  font-size: 1.2rem;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.radius.small};
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const Navbar = ({ setCursorVariant }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/chi-siamo', label: 'Chi Siamo' },
    { path: '/servizi', label: 'Servizi' },
    { path: '/portfolio', label: 'Portfolio' },
  ];

  return (
    <>
      <NavContainer 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        isScrolled={isScrolled}
      >
        <Link to="/">
          <Logo 
            isScrolled={isScrolled}
            onMouseEnter={() => setCursorVariant("text")}
            onMouseLeave={() => setCursorVariant("default")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            D-<span>Soft</span>
          </Logo>
        </Link>

        <NavLinks>
          {navItems.map((item, index) => (
            <Link key={item.path} to={item.path}>
              <NavLink
                isScrolled={isScrolled}
                $isActive={location.pathname === item.path}
                onMouseEnter={() => setCursorVariant("link")}
                onMouseLeave={() => setCursorVariant("default")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {item.label}
              </NavLink>
            </Link>
          ))}
          
          <Link to="/contatti">
            <ContactButton
              onMouseEnter={() => setCursorVariant("button")}
              onMouseLeave={() => setCursorVariant("default")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contattaci
            </ContactButton>
          </Link>
        </NavLinks>

        <MobileMenuButton
          isScrolled={isScrolled}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          onMouseEnter={() => setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant("default")}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaBars />
        </MobileMenuButton>
      </NavContainer>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <MobileMenu
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ 
                type: "spring", 
                damping: 30, 
                stiffness: 300 
              }}
            >
              <MobileMenuHeader>
                <Logo isScrolled={true}>
                  D-<span>Soft</span>
                </Logo>
                <CloseButton onClick={() => setIsMobileMenuOpen(false)}>
                  <FaTimes />
                </CloseButton>
              </MobileMenuHeader>
              
              <MobileNavLinks>
                {navItems.map((item, index) => (
                  <Link 
                    key={item.path} 
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MobileNavLink
                      $isActive={location.pathname === item.path}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                    >
                      {item.label}
                    </MobileNavLink>
                  </Link>
                ))}
                
                <Link to="/contatti" onClick={() => setIsMobileMenuOpen(false)}>
                  <MobileNavLink
                    $isActive={location.pathname === '/contatti'}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    style={{ 
                      background: '#007AFF',
                      color: 'white',
                      marginTop: '1rem'
                    }}
                  >
                    Contattaci
                  </MobileNavLink>
                </Link>
              </MobileNavLinks>
            </MobileMenu>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar; 